import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppFooter } from '../branding/AppFooter';
import { AppHeader } from '../branding/AppHeader';
import { Button } from '../components/Button/Button';
import { useProjectsStoreApi } from '../stores/projectsContext';
import './OnboardingPage.css';

/**
 * "Your first model" onboarding — `/onboarding`.
 *
 * PLAN.md exit criterion: *"a student finishes their first 15-min
 * onboarding feeling proud of what they made — not a worksheet, a
 * working classifier."*
 *
 * The wizard does the boring setup so the kid can skip straight to
 * the fun part:
 *   * Auto-creates a starter image project ("My first classifier").
 *   * Pre-seeds two classes — "thumbs up" and "thumbs down" — so the
 *     kid doesn't have to invent class names cold.
 *   * Lands them in the studio, where the existing coachmarks guide
 *     capture → train → test.
 *
 * The kid can still skip and start from scratch — this isn't a
 * mandatory route.
 */
export function OnboardingPage(): JSX.Element {
  const navigate = useNavigate();
  const store = useProjectsStoreApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStart = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const project = await store.getState().create({
        name: 'My first classifier',
        kind: 'image',
      });
      // Pre-seed two classes the kid can capture into right away.
      // The coachmarks (`first-class` then `capture-samples`) guide
      // the rest. Names are kid-friendly + easy to act out.
      await store.getState().addClass(project.id, 'thumbs up 👍');
      await store.getState().addClass(project.id, 'thumbs down 👎');
      navigate(`/studio/p/${project.id}`);
    } catch (err) {
      setBusy(false);
      setError(
        err instanceof Error ? err.message : "Couldn't set up the starter project. Try again?",
      );
    }
  };

  return (
    <div className="bl-page">
      <AppHeader subBrand="Onboarding" privacy="on-device" />
      <div className="bl-onboarding">
        <div className="bl-onboarding__card">
          <span className="bl-onboarding__eyebrow">Your first model</span>
          <h1 className="bl-onboarding__title">Train a computer to tell two things apart.</h1>
          <p className="bl-onboarding__lede">
            You&apos;ll point the camera at yourself and show it two different things — like a
            thumbs-up and a thumbs-down. Then you&apos;ll click Train, and the model will learn the
            difference. About 15 minutes.
          </p>

          <ol className="bl-onboarding__steps">
            <li className="bl-onboarding__step">
              <span className="bl-onboarding__step-num">1</span>
              <div>
                <h3 className="bl-onboarding__step-title">Capture</h3>
                <p className="bl-onboarding__step-body">
                  Hold up a thumbs-up to the camera and click capture a few times. Try different
                  angles. Then do the same with thumbs-down.
                </p>
              </div>
            </li>
            <li className="bl-onboarding__step">
              <span className="bl-onboarding__step-num">2</span>
              <div>
                <h3 className="bl-onboarding__step-title">Train</h3>
                <p className="bl-onboarding__step-body">
                  Click Train. The studio uses your samples to teach the model. Takes a few seconds.
                </p>
              </div>
            </li>
            <li className="bl-onboarding__step">
              <span className="bl-onboarding__step-num">3</span>
              <div>
                <h3 className="bl-onboarding__step-title">Test</h3>
                <p className="bl-onboarding__step-body">
                  Click Test. Show the camera a thumbs-up — does it guess right? Try fooling it.
                  Honest mistakes are interesting, not failures.
                </p>
              </div>
            </li>
          </ol>

          <div className="bl-onboarding__actions">
            <Button onClick={handleStart} disabled={busy}>
              {busy ? 'Setting up…' : "Let's go →"}
            </Button>
            <button
              type="button"
              className="bl-onboarding__skip"
              onClick={() => navigate('/studio')}
            >
              Skip — I&apos;ll start from scratch
            </button>
          </div>

          {error ? (
            <p className="bl-onboarding__error" role="alert">
              {error}
            </p>
          ) : null}

          <p className="bl-onboarding__privacy">
            Everything happens on this device. The camera doesn&apos;t send anything anywhere.
          </p>
        </div>
      </div>
      <AppFooter />
    </div>
  );
}
